import queryString from 'query-string';
import { analyticsService } from '@common/analytics';
import {
  getDirectMessagesApi, loadAdvisorAPI, loadAdvisorFeedbacksAPI, getSimilarOnlineAdvisorsApi
} from '@common/api/api';
import Const from '../config/const';
import { setApplicationError } from './aplication_error';
import { log, LOG_LEVELS } from '../../common/config/app_logger';
import { updateNavigationStack, navigationStackLoadingAction } from './application';
import { getDataFromStorage, saveDataToStorage, toCamelCase } from '../../common/config/utils';
import { authPendingAction, authPendingCloseAction, displayRegister } from './auth';
import { isPrerenderMode, getAdvisorDataIfSsrMode, verifyBusyCarouselRender } from '../config/util';
import { advisorsListUpdated } from './shared_actions';
import { subscribeAdvisorsToDB } from './firebase_realtime_database';

export const reviewsLoaded = ({
  reviews, id, initial
}) => (dispatch) => {
  dispatch({
    type: Const.advisors.reviewsLoaded,
    id,
    reviews,
    initial
  });
};

export const advisorsReviewsReset = (id) => (dispatch) => {
  dispatch({
    type: Const.advisors.reviewsReset,
    id
  });
};

export const loadAdvisorReviews = (id) => (dispatch, getState) => {
  const { advisors } = getState();
  const params = advisors[id].reviews?.paginationParams;
  dispatch({ type: Const.advisors.reviewsLoading, id });
  loadAdvisorFeedbacksAPI(id, params).then((res) => {
    dispatch(reviewsLoaded({
      id,
      initial: params === undefined,
      reviews:{
        items: res.feedbacks,
        paginationParams: res.paginationParams
      }
    }));
  }).catch((e) => log(LOG_LEVELS.ERROR, 'Advisors', 'Error load feedback API', e));
};

export const updateAdvisorNavigationStack = ({ advisor, dispatch }) => {
  dispatch(updateNavigationStack(
    {
      itemData: { slug: advisor.slug, name: advisor.nickname },
      navigationStackLoading: false
    }
  ));
};

const removeDmIdFromSearch = ({ directMessageAnalytics, history, location }) => {
  const search = queryString.parse(location.search);
  delete search.dm_id;
  const updatedSearch = queryString.stringify(search);
  const analitycsPrams = directMessageAnalytics
    ? { ...directMessageAnalytics, startChatClickSource: directMessageAnalytics.clickSource } : {};
  history(`${ location.pathname }?${ updatedSearch }`, {
    search: updatedSearch,
    state: { ...location.state, ...analitycsPrams }
  });
};

const trackProfileViewEvents = ({
  dmId, analyticsActions, conversionEventId, history, location, advisor, similarOnlineAdvisors, urgencySignalText
}) => {
  const {
    nickname, serviceTitle, profilePictureUrl, rating, readingsCount, yearJoined, profileVideoUrl, status, aiReviewsSummary
  } = advisor;
  const carouselAvailability = window.config?.available_busy_carousel === 'true';
  const shouldShowBusyCarousel = verifyBusyCarouselRender(window.currentUserId, status, carouselAvailability, similarOnlineAdvisors);
  const extraParams = {
    nickname,
    serviceTitle,
    profilePictureUrl,
    rating,
    readingsCount,
    yearJoined,
    profileVideoUrl,
    conversionEventId,
    history,
    location,
    advisorId: advisor.id,
    shouldShowBusyCarousel,
    similarOnlineAdvisors,
    aiReviewsSummary,
    urgencySignalText
  };
  if (!dmId) {
    analyticsActions.forEach((a) => a(extraParams));
    return;
  }
  getDirectMessagesApi(dmId).then(({ directMessage }) => {
    analyticsService.trackEvent('dm user connected', {
      'dm id': dmId,
      'advisor id': directMessage.advisor.id,
      'dm channel': 'email'
    });
    removeDmIdFromSearch({ directMessageAnalytics: directMessage.analytics, history, location });
    analyticsActions.forEach((a) => a(extraParams));
  }).catch(() => {
    removeDmIdFromSearch({ history, location });
    analyticsActions.forEach((a) => a(extraParams));
  });
};

const handleAnalyticsAction = ({
  analyticsparams, conversionEventId, advisor, dispatch, urgencySignalText
}) => {
  const {
    fromAdvisorProfile, analyticsActions, history, location, similarOnlineAdvisors
  } = analyticsparams;
  if (!fromAdvisorProfile) return;
  const search = toCamelCase(queryString.parse(window.location.search));
  const { dmId } = search;
  if (window.currentUserId || !dmId) {
    trackProfileViewEvents({
      dmId, analyticsActions, conversionEventId, history, location, advisor, similarOnlineAdvisors, urgencySignalText
    });
    return;
  }
  dispatch(authPendingAction(() => {
    trackProfileViewEvents({
      dmId, analyticsActions, conversionEventId, history, location, advisor, similarOnlineAdvisors, urgencySignalText
    });
  }));
  dispatch(authPendingCloseAction(() => {
    trackProfileViewEvents({
      dmId, analyticsActions, conversionEventId, history, location, advisor, similarOnlineAdvisors, urgencySignalText
    });
  }));
  dispatch(displayRegister({ shouldDisplay: true }));
};

export const loadSimilarOnlineAdvisors = (res, id) => (dispatch) => {
  const advisors = Array.isArray(res) ? res : res?.similarOnlineAdvisors;
  dispatch({
    type: Const.advisors.loadSimilarOnlineAdvisors,
    similarOnlineAdvisors: advisors,
    id
  });
};

export const getSimilarOnlineAdvisors = (id) => (dispatch) => {
  getSimilarOnlineAdvisorsApi(id).then((res) => {
    dispatch(advisorsListUpdated(res?.similarOnlineAdvisors));
    dispatch(loadSimilarOnlineAdvisors(res, id));
  });
};

const stripHtmlTags = (htmlString) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  return tempDiv.textContent || null;
};

export const trackUrgencySignalShown = (urgencyMessage) => {
  const combinedText = urgencyMessage ? `${ urgencyMessage.icon } ${ stripHtmlTags(urgencyMessage.text) }` : null;
  const currentStoredText = getDataFromStorage('advisor_urgency_signal_text');
  if (currentStoredText !== combinedText) {
    saveDataToStorage('advisor_urgency_signal_text', combinedText);
  }
};

const setUpdatedUrgencyItems = (advisor, storedUrgencyItems, urgencySignal) => {
  if (advisor?.status !== 'online' || advisor.id in storedUrgencyItems) return;
  const updatedUrgencyItems = { ...storedUrgencyItems };
  if (urgencySignal?.show && urgencySignal?.message) {
    updatedUrgencyItems[advisor.id] = urgencySignal.message;
  } else {
    updatedUrgencyItems[advisor.id] = null;
  }
  saveDataToStorage('advisor_urgency_signal', JSON.stringify(updatedUrgencyItems));
  window.dispatchEvent(new CustomEvent('advisorUrgencySignalUpdated', {
    detail: { advisorId: advisor.id, urgencySignal }
  }));
};

const urgencySignalRenderedEvent = (advisorId, analyticsParams) => {
  let analyticsTracked = false;

  const handleUrgencySignalRendered = (event) => {
    const { advisorId: renderedAdvisorId } = event.detail;
    if (renderedAdvisorId === advisorId && !analyticsTracked) {
      analyticsTracked = true;
      handleAnalyticsAction(analyticsParams);
      window.removeEventListener('urgencySignalRendered', handleUrgencySignalRendered);
    }
  };

  window.addEventListener('urgencySignalRendered', handleUrgencySignalRendered);
  setTimeout(() => {
    if (!analyticsTracked) {
      analyticsTracked = true;
      handleAnalyticsAction(analyticsParams);
    }
    window.removeEventListener('urgencySignalRendered', handleUrgencySignalRendered);
  }, 100);
};

const getAdditionalParams = (fromAdvisorProfile, loadUrgencySignal, storedUrgencyItems) => {
  const additionalParams = {};
  if (fromAdvisorProfile) {
    additionalParams.context = 'profile_page';
  }
  if (loadUrgencySignal) {
    const uniqueViewedProfilesCount = Object.keys(storedUrgencyItems).length;
    additionalParams.unique_viewed_profiles_count = uniqueViewedProfilesCount + 1;
  }
  return additionalParams;
};

export const loadAdvisor = (
  slug,
  analyticsparams = { fromAdvisorProfile : false, loadUrgencySignal : false }
) => (dispatch, getState) => {
  if (isPrerenderMode()) return;
  dispatch({ type: Const.advisors.loading });
  if (!getAdvisorDataIfSsrMode()) dispatch(navigationStackLoadingAction());
  const { fromAdvisorProfile, loadUrgencySignal } = analyticsparams;
  const storedUrgencyItems = JSON.parse(getDataFromStorage('advisor_urgency_signal') || '{}');

  const additionalParams = getAdditionalParams(fromAdvisorProfile, loadUrgencySignal, storedUrgencyItems);

  loadAdvisorAPI(slug, additionalParams).then(({
    advisor, reviews, error, conversionEventId, similarOnlineAdvisors, urgencySignal
  }) => {
    if (error) {
      dispatch(setApplicationError({ errorCode: error.errorStatus, errorUrl: error.errorUrl }));
      if (error.errorStatus !== 410) return;
    }

    setUpdatedUrgencyItems(advisor, storedUrgencyItems, urgencySignal);

    const analyticsParams = {
      advisorId: advisor.id, analyticsparams: { ...analyticsparams, advisorId: advisor.id, similarOnlineAdvisors }, conversionEventId, dispatch, advisor
    };
    urgencySignalRenderedEvent(advisor.id, analyticsParams);

    dispatch(reviewsLoaded({
      reviews, id: advisor.id, initial: true
    }));
    dispatch(subscribeAdvisorsToDB([advisor]));
    updateAdvisorNavigationStack({ advisor, dispatch, getState });
    if (similarOnlineAdvisors) {
      dispatch(advisorsListUpdated(similarOnlineAdvisors));
      dispatch(loadSimilarOnlineAdvisors(similarOnlineAdvisors, advisor.id));
    }
  }).catch((error) => {
    log(LOG_LEVELS.ERROR, 'Advisors', 'Error loadAdvisorAPI', error);
    if (error.status !== 404) {
      dispatch(setApplicationError({ errorCode: error.status, errorUrl: error.url }));
    }
  });
};

export const trackBusyCarouselEvent = (eventName, advisors) => {
  const properties = {
    'advisor ids shown': advisors?.map((adv) => adv.id)
  };
  analyticsService.trackEvent(eventName, properties);
};
